/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import { MS_URL, CLIENT_ID, AUTHORITY, API_SCOPES, API_URL } from './Components/utils/Constants';

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */

export const msalConfig = {
    auth: {
        clientId: CLIENT_ID,
        authority: `https://login.microsoftonline.com/${AUTHORITY}`,
        redirectUri: `${MS_URL}mdc/auth/media`,
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        clientCapabilities: ['CP1'],
    },
    cache: {
        cacheLocation: "localStorage", // This configures where your cache will be stored
        storeAuthStateInCookie: true, // Enable for better popup auth state communication
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        // Log errors but don't dispatch events that trigger logout
                        // COOP warnings and popup errors are expected and non-fatal
                        console.error("MSAL:", message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            }
        }
    }
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
    apiHello: {
        endpoint: `${API_URL}/*`,
        scopes: [`api://${API_SCOPES}/.default`],
    },
    // DEPRECATED: graphApi scopes should NOT be requested by frontend
    // Frontend must NOT make direct calls to Microsoft Graph API
    // All Graph API calls must go through backend using application tokens (client_credentials)
    // Backend independently acquires Graph API tokens - frontend does not need Graph API scopes
    graphApi: {
        endpoint: 'https://graph.microsoft.com/v1.0',
        scopes: ['https://graph.microsoft.com/.default'],
        // NOTE: These scopes are NOT used by frontend - kept for reference only
        // Backend uses client_credentials flow to acquire Graph API tokens independently
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 * 
 * ARCHITECTURE: Frontend requests backend API token for user authentication/validation.
 * Backend validates this token to extract user oid, then uses client_credentials for Graph API.
 * This ensures strict separation: user token for validation, application token for Graph API.
 */
export const loginRequest = {
    scopes: [...protectedResources.apiHello.scopes] // Request backend API token for user validation
};

/**
 * DEPRECATED: Graph API configuration - should NOT be used by frontend.
 * 
 * ARCHITECTURE: Frontend must NOT make direct calls to Microsoft Graph API.
 * All Graph API calls must go through the backend.
 * 
 * Backend Architecture:
 * - Frontend sends user token (delegated, contains PII) to backend for validation only
 * - Backend validates user token to extract user oid (non-PII identifier)
 * - Backend independently acquires application token via client_credentials flow
 * - Backend uses application token (contains roles, no PII) for all Graph API calls
 * 
 * If you need Graph API data, call backend API endpoints instead.
 * Backend will handle Graph API calls using application tokens.
 * 
 * @deprecated This configuration should not be used. Use backend API endpoints instead.
 */
export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
    // NOTE: This endpoint should NOT be called directly from frontend
    // All Graph API calls must go through backend
};